/** @jsx jsx */
/** @jsxFrag React.Fragment */
import {
  Badge,
  Group,
  List,
  ListItem,
  ListItemDescription,
  ListLabel,
} from '@apollo/orbit';
import { colors } from '@apollo/space-kit/colors';
import { Telescope } from '@apollo/space-kit/illustrations/Telescope';
import { jsx } from '@emotion/react';
import classnames from 'classnames';
import { flow, initial, isEqual, last, orderBy, pick } from 'lodash';
import groupBy from 'lodash/fp/groupBy';
import map from 'lodash/fp/map';
import moment from 'moment';
import React from 'react';

import { StickyHeader } from '../components/stickyHeader/StickyHeader';
import { OperationHistoryItem } from '../hooks/useExplorerState/useOperationHistory';

export const OperationHistory = ({
  operationHistory,
  loadOperation,
}: {
  loadOperation: (item: OperationHistoryItem) => void;
  operationHistory: ReadonlyArray<OperationHistoryItem>;
}) => {
  const historyGroupedByDate = flow(
    map((historyItem: OperationHistoryItem) => ({
      ...historyItem,
      timestampMoment: moment(historyItem.timestamp),
    })),
    groupBy((historyItem) => historyItem.timestampMoment.format('YYYY-MM-DD')),
  )(operationHistory);

  return (
    <List isInteractive>
      <StickyHeader className="mb-4">Run History</StickyHeader>

      {!operationHistory.length ? (
        <div className="flex flex-1 flex-col items-center justify-center p-10">
          <Telescope
            className="w-40 text-transparent"
            css={{
              '[fill="#F18686"]': {
                fill: 'transparent',
              },
              '[fill="#74B0F4"]': {
                fill: 'transparent',
              },
              '[stroke="#464646"]': {
                stroke: colors.blilet.base,
              },
              'g.stars, g[fill-rule="evenodd"] > g': {
                'path[fill="#CAD0D8"]': {
                  fill: 'transparent',
                  stroke: colors.blilet.base,
                },
                'circle[fill="#CAD0D8"]': {
                  fill: colors.blilet.base,
                },
              },
            }}
          />
          <p className="mt-6 text-center text-primary">
            Run an operation to see your history
          </p>
        </div>
      ) : (
        Object.values(historyGroupedByDate).map((historyItemsInDate) => (
          <React.Fragment key={historyItemsInDate[0]?.timestamp}>
            <ListLabel>
              {historyItemsInDate[0]?.timestampMoment.format('MMMM D')}
            </ListLabel>
            {orderBy(historyItemsInDate, (item) => item.timestamp, ['desc'])
              .reduce<
                Array<(typeof historyItemsInDate)[number] & { count: number }>
              >((groupedSimilarHistoryItems, historyItem) => {
                const lastGroupedHistoryItem = last(groupedSimilarHistoryItems);
                const fieldsToCheckForEquality: Array<
                  keyof typeof historyItem
                > = [
                  'operationString',
                  'responseStatus',
                  'variables',
                  'headers',
                  'script',
                ];
                if (
                  lastGroupedHistoryItem &&
                  isEqual(
                    pick(lastGroupedHistoryItem, fieldsToCheckForEquality),
                    pick(historyItem, fieldsToCheckForEquality),
                  )
                ) {
                  return [
                    ...initial(groupedSimilarHistoryItems),
                    {
                      ...historyItem,
                      count: lastGroupedHistoryItem.count + 1,
                    },
                  ];
                }
                return [
                  ...groupedSimilarHistoryItems,
                  {
                    ...historyItem,
                    count: 1,
                  },
                ];
              }, [])
              .map((historyItem) => (
                <ListItem
                  key={historyItem.timestamp}
                  onClick={() => loadOperation(historyItem)}
                >
                  <Group orientation="vertical" className="w-full">
                    <span className="flex flex-row items-center gap-2">
                      <span className="flex-1 truncate font-mono text-code-b">
                        {historyItem.operationName
                          ? historyItem.operationName
                          : '(unnamed operation)'}
                      </span>
                      {historyItem.responseStatus !== 200 && (
                        <span className="block size-3 rounded-full bg-btn-destructive" />
                      )}
                      <span
                        className={classnames(
                          'flex-none',
                          historyItem.responseStatus === 200
                            ? 'text-secondary'
                            : 'text-error',
                        )}
                      >
                        {historyItem.timestampMoment.format('h:mm a')}
                      </span>
                    </span>
                    <ListItemDescription className="truncate text-sm text-secondary">
                      {historyItem.operationString
                        .replace(/\s*([{:}])\s*/g, '$1')
                        .slice(0, 100)}
                    </ListItemDescription>
                    {historyItem.count > 1 && (
                      <Badge>{historyItem.count} times</Badge>
                    )}
                  </Group>
                </ListItem>
              ))}
          </React.Fragment>
        ))
      )}
    </List>
  );
};
